import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Grid,
	Typography,
	Box
} from '@material-ui/core';
import {
	Storage as StorageIcon,
	Computer as ComputerIcon,
	People as PeopleIcon,
	AccountTree as AccountTreeIcon,
	Memory as MemoryIcon,
	Speed as SpeedIcon,
	TrendingUp as TrendingUpIcon,
	TrendingDown as TrendingDownIcon
} from '@material-ui/icons';
import { getThemeColors } from '../utils/themeUtils';

const useStyles = makeStyles(theme => ({
	container: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid rgba(255, 255, 255, 0.1)',
		overflow: 'hidden',
		boxShadow: 'none',
		width: '100%',
		boxSizing: 'border-box'
	},
	metricsGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		'& > div:not(:last-child)': {
			borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
		},
		[theme.breakpoints.up('sm')]: {
			gridTemplateColumns: 'repeat(2, 1fr)',
			'& > div:not(:last-child)': {
				borderBottom: 'none'
			},
			'& > div:nth-child(odd)': {
				borderRight: '1px solid rgba(255, 255, 255, 0.1)'
			},
			'& > div:nth-child(n+3)': {
				borderTop: '1px solid rgba(255, 255, 255, 0.1)'
			}
		}
	},
	metricCard: {
		padding: '16px 20px',
		minWidth: 0,
		overflow: 'hidden'
	},
	metricLabel: {
		fontSize: '14px',
		fontWeight: 500,
		color: '#cbd5e1',
		marginBottom: '8px'
	},
	metricValueRow: {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		gap: '8px',
		[theme.breakpoints.up('md')]: {
			flexDirection: 'column',
			alignItems: 'flex-start'
		},
		[theme.breakpoints.up('lg')]: {
			flexDirection: 'row',
			alignItems: 'baseline'
		}
	},
	metricValueGroup: {
		display: 'flex',
		alignItems: 'baseline',
		gap: '8px'
	},
	metricValue: {
		fontSize: '24px',
		fontWeight: 600,
		color: '#1C75D2', // Blue accent
		lineHeight: 1.2
	},
	subValue: {
		fontSize: '13px',
		fontWeight: 500,
		color: '#94a3b8'
	},
	changeBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		borderRadius: '9999px',
		padding: '2px 10px',
		fontSize: '12px',
		fontWeight: 500,
		gap: '2px'
	},
	changeBadgeIncrease: {
		backgroundColor: 'rgba(34, 197, 94, 0.1)',
		color: '#22c55e'
	},
	changeBadgeDecrease: {
		backgroundColor: 'rgba(239, 68, 68, 0.1)',
		color: '#ef4444'
	},
	changeIcon: {
		fontSize: '14px',
		marginLeft: '-2px',
		marginRight: '2px'
	}
}));

const MetricCard = ({ title, value, subValue, change, changeType, colors }) => {
	const classes = useStyles();

	return (
		<div className={classes.metricCard} style={{ borderColor: colors.border.primary }}>
			<Typography className={classes.metricLabel} style={{ color: colors.text.secondary }}>
				{title}
			</Typography>

			<div className={classes.metricValueRow}>
				<div className={classes.metricValueGroup}>
					<Typography className={classes.metricValue} style={{ color: colors.accent.blue }}>
						{value}
					</Typography>
					{subValue && (
						<Typography className={classes.subValue} style={{ color: colors.text.tertiary }}>
							{subValue}
						</Typography>
					)}
				</div>

				{change && changeType && (
					<span 
						className={classes.changeBadge}
						style={{
							backgroundColor: changeType === 'increase' ? colors.badge.activeBg : colors.badge.inactiveBg,
							color: changeType === 'increase' ? colors.badge.activeText : colors.badge.inactiveText
						}}
					>
						{changeType === 'increase' ? (
							<TrendingUpIcon className={classes.changeIcon} />
						) : (
							<TrendingDownIcon className={classes.changeIcon} />
						)}
						{change}
					</span>
				)}
			</div>
		</div>
	);
};

const DashboardMetricsCards = ({ metrics, themeMode = 'dark' }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);

	if (!metrics) return null;

	return (
		<div 
			className={classes.container}
			style={{
				backgroundColor: colors.card.background,
				border: `1px solid ${colors.card.border}`,
				boxShadow: colors.card.shadow
			}}
		>
			<div className={classes.metricsGrid} style={{ 
				'& > div:not(:last-child)': { borderColor: colors.border.primary },
				'& > div:nth-child(odd)': { borderColor: colors.border.primary },
				'& > div:nth-child(n+3)': { borderColor: colors.border.primary }
			}}>
				{/* Clusters Card */}
				<MetricCard
					title="Total Clusters"
					value={metrics.totalClusters || 0}
					subValue={`from ${(metrics.totalClusters || 0) - (metrics.activeClusters || 0)}`}
					change={metrics.activeClusters || 0}
					changeType="increase"
					colors={colors}
				/>

				{/* Users Card */}
				<MetricCard
					title="Total Users"
					value={metrics.totalUsers || 0}
					subValue={`from ${(metrics.totalUsers || 0) - (metrics.activeUsers || 0)}`}
					change={metrics.activeUsers || 0}
					changeType="increase"
					colors={colors}
				/>

				{/* Stacks Card */}
				<MetricCard
					title="Total Stacks"
					value={metrics.totalStacks || 0}
					subValue={`from ${(metrics.totalStacks || 0) - (metrics.activeStacks || 0)}`}
					change={metrics.activeStacks || 0}
					changeType="increase"
					colors={colors}
				/>

				{/* GPU Utilization Card */}
				<MetricCard
					title="GPU Utilization"
					value={`${(metrics.avgGpuUtilization || 0).toFixed(1)}%`}
					subValue={`${metrics.totalGPUs || 0} GPUs`}
					colors={colors}
				/>

				{/* CPU Utilization Card */}
				<MetricCard
					title="CPU Utilization"
					value={`${(metrics.avgCpuUtilization || 0).toFixed(1)}%`}
					subValue={`${metrics.totalNodes || 0} nodes`}
					colors={colors}
				/>

				{/* Memory Utilization Card */}
				<MetricCard
					title="Memory Utilization"
					value={`${(metrics.avgMemoryUtilization || 0).toFixed(1)}%`}
					subValue="avg"
					colors={colors}
				/>
			</div>
		</div>
	);
};

// Memoize to prevent unnecessary re-renders when metrics haven't changed
export default React.memo(DashboardMetricsCards, (prevProps, nextProps) => {
	return prevProps.themeMode === nextProps.themeMode &&
		JSON.stringify(prevProps.metrics) === JSON.stringify(nextProps.metrics);
});

